import React, { useCallback, useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import {
  facebookScraper,
  youtubeScraper,
  instagramScraper,
  fetchSocialMediaTasks,
} from "../features/scrap/scrapeSlice";
import { deleteTask } from "../features/tasks/taskSlice";
import LoadingComponent from "../components/common/LoadingComponent";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { confirmationModal } from "../constants/confirmation.constants";
import { formatDate } from "./Meetings/Meetings.utils";
import { STATUS_BADGE } from "../constants/constants";

const SOURCES = [
  {
    value: "facebook",
    label: "Facebook Page",
    icon: "bi-facebook",
    color: "#1877f2",
  },
  { value: "youtube", label: "YouTube", icon: "bi-youtube", color: "#ff0000" },
  {
    value: "instagram",
    label: "Instagram",
    icon: "bi-instagram",
    color: "#E4405F",
  },
];

const PLACEHOLDERS = {
  facebook: "https://www.facebook.com/pagename",
  youtube: "https://www.youtube.com/@channel",
  instagram: "https://www.instagram.com/username",
};

const getSourceLabel = (source) =>
  SOURCES.find((item) => item.value === source)?.label || source;

const getTaskDocuments = (response) => {
  const payload = response?.data ?? response;
  const candidates = [
    payload,
    payload?.tasks,
    payload?.data,
    payload?.result,
    payload?.result?.tasks,
  ];
  return candidates.find(Array.isArray) || [];
};

const validateUrlForSource = (url, source) => {
  if (!url) return false;

  const lower = url.trim().toLowerCase();

  if (source === "facebook") {
    return lower.includes("facebook.com");
  }

  if (source === "instagram") {
    return lower.includes("instagram.com");
  }

  if (source === "youtube") {
    return lower.includes("youtube.com") || lower.includes("youtu.be");
  }

  return false;
};

const buildValidationSchema = (source) =>
  Yup.object({
    url: Yup.string()
      .trim()
      .required("URL is required")
      .test(
        "starts-with-http",
        "URL must start with http:// or https://",
        (val) => !val || /^https?:\/\//i.test(val.trim()),
      )
      .test(
        "correct-platform",
        source === "facebook"
          ? "Please enter a valid Facebook URL"
          : source === "instagram"
            ? "Please enter a valid Instagram URL"
            : "Please enter a valid YouTube URL",
        (val) => !val || validateUrlForSource(val, source),
      ),
  });

const ScrapingTaskCard = ({ task, onDelete, onRefresh }) => {
  const sourceMeta =
    SOURCES.find((s) =>
      task.title?.includes(
        s.value === "facebook"
          ? "facebook.com"
          : s.value === "instagram"
            ? "instagram.com"
            : "youtube.com",
      ),
    ) || SOURCES[0];

  const badge = STATUS_BADGE[task?.status] || STATUS_BADGE.PENDING;
  const actionCount = task?.actionsCount ?? 0;

  return (
    <div
      className="card h-100 border rounded-3 scraping-task-card shadow-sm-hover"
      style={{ transition: "box-shadow .15s, border-color .15s" }}
    >
      <div className="card-body p-3 d-flex flex-column">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="d-flex align-items-center gap-2">
            <i
              className={`bi ${sourceMeta.icon}`}
              style={{ color: sourceMeta.color, fontSize: 16 }}
            />
            <span
              className={`badge rounded-pill ${badge.cls}`}
              style={{ fontSize: 10.5, padding: "3px 8px" }}
            >
              {badge.label}
            </span>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-sm btn-link text-primary p-0 text-decoration-none"
              title="Rerun scraper"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRefresh(task);
              }}
            >
              <i className="bi bi-arrow-clockwise" style={{ fontSize: 15 }} />
            </button>

            <button
              type="button"
              className="btn btn-sm btn-link text-danger p-0 text-decoration-none"
              title="Delete this scraping task"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete(task);
              }}
            >
              <i className="bi bi-trash3" style={{ fontSize: 13 }} />
            </button>
          </div>
        </div>

        <Link
          to={`/social-media-scraping/${task?.uuid}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-decoration-none text-reset flex-grow-1 d-flex flex-column"
          style={{ cursor: "pointer" }}
        >
          <p
            className="fw-semibold text-body mb-2 text-truncate"
            style={{ fontSize: 13, lineHeight: 1.35 }}
            title={task?.title}
          >
            {task?.title}
          </p>

          <div
            className="d-flex flex-wrap align-items-center gap-2 mt-auto pt-2 text-muted"
            style={{ fontSize: 11 }}
          >
            <span className="text-nowrap">
              <i className="bi bi-calendar3 me-1" />
              {formatDate(task?.createdAt)}
            </span>

            <div
              className="d-flex flex-wrap align-items-center gap-2 ms-auto"
              style={{ marginRight: "unset !important" }}
            >
              <span
                className="badge bg-primary-subtle text-primary-emphasis border border-primary-subtle rounded-pill fw-normal text-nowrap"
                style={{ fontSize: 10.5 }}
              >
                {actionCount} Actions
              </span>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
};

function SocialMediaScraping() {
  const dispatch = useDispatch();

  const {
    loading: scrapeLoading,
    tasksLoading,
    socialMediaTasks,
  } = useSelector((s) => s.scrape);

  const [source, setSource] = useState("facebook");

  const taskCards = getTaskDocuments(socialMediaTasks);

  const loadCards = useCallback(
    async (src) => {
      const result = await dispatch(fetchSocialMediaTasks(src));
      if (fetchSocialMediaTasks.rejected.match(result)) {
        toast.error("Unable to load scraping tasks.");
      }
    },
    [dispatch],
  );

  useEffect(() => {
    loadCards(source);
  }, [source, loadCards]);

  const handleCardDelete = useCallback(
    async (task) => {
      const confirm = await confirmationModal(
        "Delete this scraping task?",
        "This will permanently remove it.",
      );
      if (!confirm.isConfirmed) return;

      const res = await dispatch(deleteTask(task.uuid));
      if (deleteTask.fulfilled.match(res)) {
        toast.success("Scraping task deleted.");
      } else {
        toast.error(res.payload?.message || "Failed to delete task.");
      }
    },
    [dispatch],
  );

  const handleScrape = async (values, { resetForm }) => {
    const rawUrl = values.url.trim();
    if (!rawUrl) return;
    try {
      const thunk =
        source === "facebook"
          ? facebookScraper
          : source === "instagram"
            ? instagramScraper
            : youtubeScraper;
      const res = await dispatch(thunk(rawUrl)).unwrap();
      toast.success(res?.data?.message || res?.message || "Scraping started.");
      resetForm();
      await loadCards(source);
    } catch (error) {
      toast.error(
        error?.message ||
          error?.data?.message ||
          `Failed to start ${getSourceLabel(source)} scraping.`,
      );
    }
  };

  const handleRefreshCard = useCallback(
    async (task) => {
      try {
        const isFacebook = task.title?.includes("facebook.com");
        const isInstagram = task.title?.includes("instagram.com");

        const scraper = isFacebook
          ? facebookScraper
          : isInstagram
            ? instagramScraper
            : youtubeScraper;

        const res = await dispatch(scraper(task.title)).unwrap();

        toast.success(
          res?.data?.message || res?.message || "Scraping started.",
        );

        await loadCards(source);
      } catch (error) {
        toast.error(
          error?.message || error?.data?.message || "Failed to start scraping.",
        );
      }
    },
    [dispatch, loadCards, source],
  );

  const selectedSourceMeta = SOURCES.find((s) => s.value === source);

  return (
    <div
      className="content"
      style={{ maxWidth: "100%", padding: "4px 20px", marginTop: "40px" }}
    >
      <div className="pb-3 pt-3">
        <div className="bg-body-emphasis rounded-3 p-4 border">
          <h3 className="mb-1 fw-semibold d-flex align-items-center gap-2 text-body">
            <i
              className={`bi ${selectedSourceMeta?.icon}`}
              style={{ color: selectedSourceMeta?.color }}
            />
            Social Media Scraping
          </h3>
          <p className="text-muted mb-0">
            Select a platform, paste the page URL, and review the tasks below.
          </p>

          <Formik
            key={source}
            initialValues={{ url: "" }}
            validationSchema={buildValidationSchema(source)}
            onSubmit={handleScrape}
          >
            {({ errors, touched }) => (
              <Form>
                <div className="d-flex align-items-start gap-3 mt-4">
                  <div style={{ minWidth: 170 }}>
                    <select
                      id="social-media-source"
                      className="form-select"
                      value={source}
                      onChange={(e) => setSource(e.target.value)}
                    >
                      {SOURCES.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="w-100">
                    <Field
                      type="text"
                      name="url"
                      autoComplete="off"
                      className={`form-control ${errors.url && touched.url ? "is-invalid" : ""}`}
                      placeholder={PLACEHOLDERS[source]}
                    />
                    <ErrorMessage
                      name="url"
                      component="div"
                      className="text-danger mt-1"
                      style={{ fontSize: 12 }}
                    />
                  </div>

                  <button
                    type="submit"
                    id="social-media-scrape-btn"
                    className="btn btn-primary d-inline-flex align-items-center gap-2"
                    disabled={scrapeLoading}
                  >
                    {scrapeLoading ? "Starting…" : "Scrape"}
                    {scrapeLoading && (
                      <LoadingComponent
                        loading
                        size={5}
                        color="currentColor"
                        wrapperStyle={{
                          display: "inline-flex",
                          alignItems: "center",
                          lineHeight: 0,
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      <div className="pb-3">
        <div className="bg-body-emphasis rounded-3 p-4 border">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="mb-0 fw-semibold d-flex align-items-center gap-2">
              <i className="bi bi-grid-3x3-gap" />
              {selectedSourceMeta?.label} Tasks
              <span
                className="badge bg-secondary-subtle text-secondary-emphasis rounded-pill fw-normal"
                style={{ fontSize: 12 }}
              >
                {taskCards.length}
              </span>
            </h5>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => loadCards(source)}
              disabled={tasksLoading}
            >
              <i className="bi bi-arrow-clockwise me-1" />
              Refresh
            </button>
          </div>

          {tasksLoading ? (
            <div className="d-flex justify-content-center py-5">
              <LoadingComponent loading size={15} />
            </div>
          ) : taskCards.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox fs-1 d-block mb-2" />
              <p className="mb-0">
                No {selectedSourceMeta?.label} scraping tasks found.
              </p>
            </div>
          ) : (
            <div className="row g-3">
              {taskCards.map((task) => (
                <div
                  className="col-12 col-sm-6 col-md-4 col-xl-3"
                  key={task.uuid}
                >
                  <ScrapingTaskCard
                    task={task}
                    onDelete={handleCardDelete}
                    onRefresh={handleRefreshCard}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SocialMediaScraping;
